import React, { useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  Link,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import HistoryDateDialog from './HistoryDateDialog';
import {
  CONTROL_STATE,
  MESSAGE_TEXT_LIMIT,
  NEW_COMMUNICATION_LIMIT,
  NEW_COMMUNICATION_TOOLTIP,
  QUICK_REFINE,
  RELATIONSHIP_FIELDS,
  RELATIONSHIP_TOOLTIP,
  SPEAKER_NAME_LIMIT,
  externalHref,
  formatHistoryDate,
  formatVersionTime,
  isNearFullCopy,
  prospectOptionLabel,
} from './engageSession';
const EMPTY = '—';

const panelSx = {
  bgcolor: '#fff',
  border: '1px solid #e6eaf0',
  borderRadius: 2,
  p: 2.5,
  minWidth: 0,
};

const StepTitle = ({ number, title, action }) => (
  <Box display='flex' alignItems='center' gap={1} mb={2}>
    <Box
      sx={{
        width: 26,
        height: 26,
        borderRadius: '50%',
        bgcolor: 'primary.main',
        color: '#fff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 13,
        fontWeight: 700,
        flexShrink: 0,
      }}
    >
      {number}
    </Box>
    <Typography variant='subtitle1' fontWeight={700}>
      {title}
    </Typography>
    {action}
  </Box>
);

const ContextRow = ({ label, value }) => (
  <Box mb={1.5}>
    <Typography variant='body2' color='text.secondary' fontWeight={600}>
      {label}
    </Typography>
    <Typography variant='body2' fontWeight={600}>
      {value || EMPTY}
    </Typography>
  </Box>
);

const InfoValue = ({ label, value }) => {
  const text = String(value || '').trim();
  const href = label === 'Website' || label === 'LinkedIn' ? externalHref(text) : '';

  return (
    <Box display='grid' gridTemplateColumns='88px minmax(0, 1fr)' columnGap={1} py={0.6} alignItems='start'>
      <Typography variant='body2' color='text.secondary'>
        {label}
      </Typography>
      {href ? (
        <Link
          href={href}
          target='_blank'
          rel='noopener noreferrer'
          underline='hover'
          variant='body2'
          sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, minWidth: 0, wordBreak: 'break-all' }}
        >
          {text}
          <OpenInNewIcon sx={{ fontSize: 14, flexShrink: 0 }} />
        </Link>
      ) : (
        <Typography variant='body2' fontWeight={text ? 600 : 500} color={text ? 'text.primary' : 'text.secondary'}>
          {text || EMPTY}
        </Typography>
      )}
    </Box>
  );
};

const EmptyPanel = ({ icon, title, subtitle }) => (
  <Box
    display='flex'
    flexDirection='column'
    alignItems='center'
    justifyContent='center'
    textAlign='center'
    py={3}
    px={2}
  >
    <Box
      sx={{
        width: 42,
        height: 42,
        borderRadius: '50%',
        bgcolor: '#e8eef7',
        color: '#90a4c4',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        mb: 1.25,
      }}
    >
      {icon}
    </Box>
    <Typography variant='body2' fontWeight={700}>
      {title}
    </Typography>
    {subtitle ? (
      <Typography variant='caption' color='text.secondary' sx={{ mt: 0.5, maxWidth: 360 }}>
        {subtitle}
      </Typography>
    ) : null}
  </Box>
);

const clippedFieldSx = (background) => ({
  bgcolor: background,
  '& .MuiInputBase-root': { alignItems: 'flex-start' },
  '& textarea': { overflow: 'auto !important', resize: 'none' },
});

const HistoryItem = ({ item, disabled, onChange, onEditDate }) => {
  const isLeadAgent = item.speakerRole === 'leadAgent';

  return (
    <Box
      display='grid'
      gridTemplateColumns={{ xs: '1fr', md: '188px minmax(0, 1fr)' }}
      columnGap={1}
      rowGap={0.75}
      alignItems='stretch'
    >
      <Box display='flex' flexDirection='column' gap={0.75} minWidth={0}>
        <TextField
          fullWidth
          size='small'
          placeholder='Person'
          disabled={disabled}
          value={item.speakerName || ''}
          onChange={(event) => onChange({ speakerName: event.target.value.slice(0, SPEAKER_NAME_LIMIT) })}
          sx={{ bgcolor: '#fff' }}
        />
        <Select
          fullWidth
          size='small'
          disabled={disabled}
          value={item.speakerRole || 'unknown'}
          onChange={(event) => onChange({ speakerRole: event.target.value === 'unknown' ? null : event.target.value })}
          sx={{ bgcolor: '#fff' }}
          inputProps={{ 'aria-label': `Role for ${item.speakerName || 'this message'}` }}
        >
          <MenuItem value='leadAgent'>Lead Agent</MenuItem>
          <MenuItem value='prospect'>Prospect</MenuItem>
          <MenuItem value='unknown'>Unknown</MenuItem>
        </Select>
        <TextField
          fullWidth
          size='small'
          placeholder='Add date and time'
          disabled={disabled}
          value={formatHistoryDate(item.occurredAt)}
          onClick={() => {
            if (!disabled) onEditDate(item._id);
          }}
          InputProps={{ readOnly: true }}
          inputProps={{ 'aria-label': `Date and time for ${item.speakerName || 'this message'}` }}
          sx={{ bgcolor: '#fff' }}
        />
      </Box>
      <TextField
        fullWidth
        multiline
        minRows={4}
        size='small'
        disabled={disabled}
        value={item.text || ''}
        error={!String(item.text || '').trim()}
        onChange={(event) => onChange({ text: event.target.value.slice(0, MESSAGE_TEXT_LIMIT) })}
        sx={{
          height: '100%',
          bgcolor: isLeadAgent ? '#e8f1fd' : '#eef3f8',
          '& .MuiInputBase-root': { height: '100%', alignItems: 'flex-start', bgcolor: 'transparent' },
          '& textarea': { height: '100% !important', overflow: 'auto !important', resize: 'none' },
        }}
      />
    </Box>
  );
};

const HistoryPanel = ({
  prospectSelected,
  historyItems,
  historyStatus,
  historyLocked,
  onHistoryChange,
  onClearHistory,
}) => {
  const [dateMessageId, setDateMessageId] = useState('');
  const dateMessage = historyItems.find((item) => item._id === dateMessageId) || null;
  const updateMessage = (id, patch) => {
    onHistoryChange(historyItems.map((item) => (item._id === id ? { ...item, ...patch } : item)));
  };
  let body;
  if (!prospectSelected) {
    body = (
      <EmptyPanel
        icon={<ChatBubbleOutlineIcon fontSize='small' />}
        title='Select a prospect to view conversation history.'
        subtitle='Choose a prospect from the dropdown to see past messages and continue the conversation.'
      />
    );
  } else if (historyStatus === 'loading') {
    body = (
      <Box display='flex' justifyContent='center' py={4}>
        <CircularProgress size={22} />
      </Box>
    );
  } else if (historyStatus === 'error') {
    body = (
      <EmptyPanel
        icon={<ChatBubbleOutlineIcon fontSize='small' />}
        title='Could not load conversation history.'
        subtitle='Try again in a moment. Nothing was changed.'
      />
    );
  } else if (!historyItems.length) {
    body = (
      <EmptyPanel
        icon={<ChatBubbleOutlineIcon fontSize='small' />}
        title='There is no history yet for this prospect.'
        subtitle='Confirmed messages are saved here for future replies.'
      />
    );
  } else {
    body = (
      <Box display='flex' flexDirection='column' gap={1.25}>
        {historyItems.map((item) => (
          <HistoryItem
            key={item._id}
            item={item}
            disabled={historyLocked}
            onChange={(patch) => updateMessage(item._id, patch)}
            onEditDate={setDateMessageId}
          />
        ))}
      </Box>
    );
  }

  return (
    <Box>
      <Box display='flex' alignItems='center' justifyContent='space-between' mb={1} minHeight={32}>
        <Typography variant='body2' fontWeight={700}>
          Conversation history
        </Typography>
        {historyItems.length > 0 && historyStatus === 'ready' ? (
          <Button
            size='small'
            color='inherit'
            disabled={historyLocked}
            onClick={onClearHistory}
            startIcon={<DeleteOutlineIcon sx={{ fontSize: 16 }} />}
            sx={{ textTransform: 'none', color: 'text.secondary', fontWeight: 600, minWidth: 0 }}
          >
            Clear history
          </Button>
        ) : null}
      </Box>
      <Box bgcolor='#f8fafc' border='1px solid #e6eaf0' borderRadius={2} p={1} height={360} overflow='auto'>
        {body}
      </Box>
      <HistoryDateDialog
        open={Boolean(dateMessage)}
        value={dateMessage?.occurredAt}
        onClose={() => setDateMessageId('')}
        onClear={() => {
          updateMessage(dateMessage._id, { occurredAt: null });
          setDateMessageId('');
        }}
        onApply={(occurredAt) => {
          updateMessage(dateMessage._id, { occurredAt });
          setDateMessageId('');
        }}
      />
    </Box>
  );
};

const EngageWorkspace = ({
  clientName,
  productName,
  prospects,
  selectedProspect,
  relationship,
  onRelationshipChange,
  onSelectProspect,
  historyItems,
  historyStatus,
  historyLocked,
  onHistoryChange,
  onClearHistory,
  newCommunication,
  onNewCommunicationChange,
  generateState,
  generatePending,
  generateLabel,
  generateInfo,
  onGenerate,
  refineState,
  refineActionState,
  refinePending,
  refineText,
  onRefineTextChange,
  onRefine,
  messageText = '',
  versions = [],
  onMessageTextChange,
  messageEditable,
  onRequestCopy,
  onSelectVersion,
}) => {
  const prospectSelected = Boolean(selectedProspect);
  const generateDisabled = generateState !== CONTROL_STATE.ELIGIBLE;
  const refineDisabled = refineState !== CONTROL_STATE.ELIGIBLE;
  const refineActionDisabled = refineActionState !== CONTROL_STATE.ELIGIBLE;
  const messageLocked = !prospectSelected || historyLocked || !messageEditable;
  const productLabel = selectedProspect?.program?.productName || productName;
  const infoRows = [
    ['Name', selectedProspect?.contactName],
    ['Title', selectedProspect?.title],
    ['Company', selectedProspect?.company],
    ['Website', selectedProspect?.website],
    ['Product', productLabel],
    ['LinkedIn', selectedProspect?.linkedin],
  ];

  return (
    <Box px={{ xs: 2, md: 3 }} py={3} minWidth={0}>
      <Box mb={2.5}>
        <Typography variant='h5' fontWeight={700}>
          Engage
        </Typography>
        <Typography variant='body2' color='text.secondary'>
          Create and refine prospect messages using your company profile and saved conversation context.
        </Typography>
      </Box>

      <Box display='grid' gridTemplateColumns={{ xs: '1fr', lg: '340px minmax(0, 1fr)' }} gap={2} alignItems='stretch'>
        <Box sx={{ ...panelSx, display: 'flex', flexDirection: 'column' }}>
          <StepTitle number='1' title='Select prospect' />
          <ContextRow label='Client' value={clientName} />
          <ContextRow label='Product' value={productName} />
          <Box mb={2.5}>
            <Typography variant='body2' color='text.secondary' fontWeight={600} mb={0.75}>
              Prospect
            </Typography>
            <Select
              fullWidth
              size='small'
              displayEmpty
              disabled={historyLocked}
              value={selectedProspect ? String(selectedProspect._id) : ''}
              onChange={(event) => onSelectProspect(event.target.value)}
              renderValue={(selected) => {
                if (!selected) return <Typography color='text.secondary'>Select prospect</Typography>;
                const match = prospects.find((row) => String(row._id) === String(selected));
                return match ? prospectOptionLabel(match) : 'Select prospect';
              }}
            >
              <MenuItem value='' sx={{ display: 'none' }}>
                Select prospect
              </MenuItem>
              {prospects.map((row) => (
                <MenuItem key={row._id} value={String(row._id)}>
                  {prospectOptionLabel(row)}
                </MenuItem>
              ))}
            </Select>
          </Box>

          <Box border='1px solid #e6eaf0' borderRadius={2} p={2} mb={2}>
            <Typography variant='body2' fontWeight={700} mb={1}>
              Prospect information
            </Typography>
            {infoRows.map(([label, value]) => (
              <InfoValue key={label} label={label} value={prospectSelected ? value : ''} />
            ))}
          </Box>

          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            <Box display='flex' alignItems='center' gap={0.5} mb={1.5}>
              <Typography variant='body2' fontWeight={700}>
                Relationship & relevance
              </Typography>
              <Tooltip title={RELATIONSHIP_TOOLTIP} arrow>
                <IconButton size='small' aria-label='About relationship and relevance'>
                  <InfoOutlinedIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                </IconButton>
              </Tooltip>
            </Box>
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1.5, minHeight: 0 }}>
              {RELATIONSHIP_FIELDS.map((field) => (
                <Box key={field.key} sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                  <Typography variant='body2' fontWeight={600} mb={0.75}>
                    {field.label}
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    minRows={3}
                    size='small'
                    placeholder={prospectSelected ? '' : field.placeholder}
                    value={prospectSelected ? relationship[field.key] || '' : ''}
                    disabled={!prospectSelected}
                    onChange={(event) => onRelationshipChange(field.key, event.target.value)}
                    sx={{
                      ...clippedFieldSx(),
                      flex: 1,
                      display: 'flex',
                      flexDirection: 'column',
                      '& .MuiInputBase-root': { flex: 1, alignItems: 'flex-start' },
                      '& textarea': { height: '100% !important', overflow: 'auto !important', resize: 'none' },
                    }}
                  />
                </Box>
              ))}
            </Box>
          </Box>
        </Box>

        <Box sx={panelSx}>
          <StepTitle number='2' title='Generate message' />
          <HistoryPanel
            prospectSelected={prospectSelected}
            historyItems={historyItems}
            historyStatus={historyStatus}
            historyLocked={historyLocked}
            onHistoryChange={onHistoryChange}
            onClearHistory={onClearHistory}
          />

          <Box mt={2.5}>
            <Box display='flex' alignItems='center' gap={0.5} mb={1}>
              <Typography variant='body2' fontWeight={700}>
                New communication (optional)
              </Typography>
              <Tooltip
                title={NEW_COMMUNICATION_TOOLTIP}
                arrow
                slotProps={{ tooltip: { sx: { maxWidth: 360, whiteSpace: 'pre-line' } } }}
              >
                <IconButton size='small' aria-label='About new communication'>
                  <InfoOutlinedIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                </IconButton>
              </Tooltip>
            </Box>
            <Box position='relative'>
              <TextField
                fullWidth
                multiline
                minRows={4}
                maxRows={4}
                disabled={!prospectSelected || historyLocked || historyStatus !== 'ready'}
                value={prospectSelected ? newCommunication : ''}
                onChange={(event) => onNewCommunicationChange(event.target.value.slice(0, NEW_COMMUNICATION_LIMIT))}
                placeholder='Paste messages or entire email/LinkedIn threads here...'
                inputProps={{ 'aria-label': 'New communication', maxLength: NEW_COMMUNICATION_LIMIT }}
                sx={{ ...clippedFieldSx(), '& .MuiInputBase-root': { pb: 2.5, alignItems: 'flex-start' } }}
              />
              <Typography
                variant='caption'
                color={newCommunication.length >= NEW_COMMUNICATION_LIMIT ? 'error' : 'text.disabled'}
                sx={{ position: 'absolute', right: 12, bottom: 8 }}
              >
                {prospectSelected ? newCommunication.length : 0}/{NEW_COMMUNICATION_LIMIT}
              </Typography>
            </Box>
          </Box>

          <Box display='flex' alignItems='center' gap={0.5} mt={2}>
            <Button
              fullWidth
              variant='contained'
              disabled={generateDisabled || generatePending || refinePending}
              startIcon={generatePending ? <CircularProgress size={16} color='inherit' /> : <AutoAwesomeIcon />}
              onClick={onGenerate}
              sx={{
                textTransform: 'none',
                fontWeight: 700,
                py: 1.2,
                boxShadow: 'none',
                '&.Mui-disabled': { bgcolor: '#bfdbfe', color: '#fff' },
              }}
            >
              {generateLabel}
            </Button>
            <Tooltip title={generateInfo} arrow>
              <IconButton size='small' aria-label='About generate message'>
                <InfoOutlinedIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
              </IconButton>
            </Tooltip>
          </Box>

          <Box display='grid' gridTemplateColumns={{ xs: '1fr', md: 'minmax(0, 2fr) minmax(0, 1fr)' }} gap={2} mt={2.5}>
            <Box minWidth={0}>
              <Typography variant='body2' fontWeight={700}>
                Message
              </Typography>
              <Typography variant='caption' color='text.secondary' display='block' mb={1}>
                Make it yours. Edit the message directly until you’re completely happy with it, or use Refine to create
                another version.
              </Typography>
              <Box position='relative'>
                <TextField
                  fullWidth
                  multiline
                  minRows={6}
                  maxRows={6}
                  disabled={messageLocked}
                  value={prospectSelected ? messageText : ''}
                  onChange={(event) => onMessageTextChange(event.target.value.slice(0, MESSAGE_TEXT_LIMIT))}
                  placeholder='Your generated message will appear here...'
                  inputProps={{
                    'aria-label': 'Message',
                    maxLength: MESSAGE_TEXT_LIMIT,
                    onCopy: (event) => {
                      const field = event.target;
                      const selected = String(field.value || '').slice(field.selectionStart, field.selectionEnd);
                      if (!isNearFullCopy(field.value, selected)) return;
                      event.preventDefault();
                      onRequestCopy();
                    },
                  }}
                  sx={{
                    ...clippedFieldSx(),
                    '& .MuiInputBase-input': { pb: '40px' },
                  }}
                />
                <IconButton
                  size='small'
                  disabled={messageLocked || !String(messageText || '').trim()}
                  aria-label='Copy message'
                  onClick={onRequestCopy}
                  sx={{ position: 'absolute', right: 24, bottom: 6 }}
                >
                  <ContentCopyIcon fontSize='small' />
                </IconButton>
              </Box>

              <Typography variant='body2' fontWeight={700} mt={2} mb={1}>
                Refine this message
              </Typography>
              <TextField
                fullWidth
                size='small'
                disabled={refineDisabled}
                value={refineDisabled ? '' : refineText}
                onChange={(event) => onRefineTextChange(event.target.value)}
                placeholder='Tell me what you’d like to improve...'
                InputProps={{
                  endAdornment: (
                    <InputAdornment position='end'>
                      <Button
                        size='small'
                        variant='contained'
                        disabled={refineActionDisabled || refinePending || generatePending}
                        startIcon={
                          refinePending ? (
                            <CircularProgress size={14} color='inherit' />
                          ) : (
                            <AutoAwesomeIcon sx={{ fontSize: 16 }} />
                          )
                        }
                        onClick={() => onRefine()}
                        sx={{
                          textTransform: 'none',
                          boxShadow: 'none',
                          '&.Mui-disabled': { bgcolor: '#e2e8f0', color: '#94a3b8' },
                        }}
                      >
                        Refine
                      </Button>
                    </InputAdornment>
                  ),
                }}
              />
              <Box display='flex' flexWrap='wrap' gap={1} mt={1.25}>
                {QUICK_REFINE.map((action) => (
                  <Chip
                    key={action.label}
                    label={action.label}
                    variant='outlined'
                    disabled={refineDisabled || refinePending || generatePending}
                    onClick={() => onRefine(action.instruction)}
                    sx={{ borderRadius: 5 }}
                  />
                ))}
              </Box>
            </Box>

            <Box
              border='1px solid #e6eaf0'
              borderRadius={2}
              bgcolor='#f8fafc'
              p={2}
              height={260}
              overflow='hidden'
              display='flex'
              flexDirection='column'
            >
              <Typography variant='body2' fontWeight={700} mb={1}>
                Version history
              </Typography>
              {versions.length ? (
                <Box display='flex' flexDirection='column' gap={1} flex={1} minHeight={0} overflow='auto' pr={0.5}>
                  {versions.map((version) => (
                    <Tooltip
                      key={version.id}
                      arrow
                      placement='left'
                      title={
                        <Box>
                          <Typography variant='caption' fontWeight={700} display='block' mb={0.5}>
                            {version.title}
                          </Typography>
                          <Typography variant='caption' sx={{ whiteSpace: 'pre-wrap' }}>
                            {version.message}
                          </Typography>
                        </Box>
                      }
                    >
                      <Box
                        role='button'
                        tabIndex={0}
                        aria-label={version.title}
                        onClick={() => !historyLocked && onSelectVersion(version.id)}
                        onKeyDown={(event) => {
                          if (historyLocked) return;
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            onSelectVersion(version.id);
                          }
                        }}
                        sx={{
                          border: '1px solid #e6eaf0',
                          borderRadius: 1.5,
                          bgcolor: '#fff',
                          px: 1.25,
                          py: 1,
                          minWidth: 0,
                          flexShrink: 0,
                          overflow: 'hidden',
                          cursor: historyLocked ? 'default' : 'pointer',
                        }}
                      >
                        <Typography variant='body2' fontWeight={700} noWrap>
                          {version.title}
                        </Typography>
                        {version.createdAt ? (
                          <Typography variant='caption' color='text.secondary' display='block' noWrap>
                            {formatVersionTime(version.createdAt)}
                          </Typography>
                        ) : null}
                      </Box>
                    </Tooltip>
                  ))}
                </Box>
              ) : (
                <EmptyPanel
                  icon={<DescriptionOutlinedIcon fontSize='small' />}
                  title='No versions yet.'
                  subtitle={
                    prospectSelected
                      ? 'Each generated message and rewrite will appear here.'
                      : 'Generate a message to see version history here.'
                  }
                />
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default EngageWorkspace;
